import React, { useState } from 'react';
import { Box } from '@mui/material';
import {
  MarketShell, useMarketData, regionLine, LineChartBox, BarChartBox,
  REGION_COLORS, REGION_LABELS, NEM_COLOR,
} from './_shared';
import { ChartSection, StatCard, formatAUD, CHART_PALETTE } from '../charts/shared';

const BANDS = ['<0', '0-50', '50-100', '100-300', '300-1000', '1000+'];

export default function MarketPrice() {
  const [days, setDays] = useState(365);
  const [region, setRegion] = useState('NEM');
  const { data, loading, error } = useMarketData('price', days);

  const isNem = region === 'NEM';
  const scopeLabel = isNem ? 'NEM average' : (REGION_LABELS[region] || region);
  const lineColor = isNem ? NEM_COLOR : REGION_COLORS[region];

  let body = null;
  if (data) {
    const kpis = data.kpis || [];
    const mean = (arr) => (arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : null);
    let avg, pctHigh, pctNeg;
    if (isNem) {
      avg = mean(kpis.map((k) => k.avg_rrp).filter((v) => v != null));
      pctHigh = mean(kpis.map((k) => k.pct_high).filter((v) => v != null));
      pctNeg = mean(kpis.map((k) => k.pct_neg).filter((v) => v != null));
    } else {
      const k = kpis.find((x) => x.region === region) || {};
      avg = k.avg_rrp; pctHigh = k.pct_high; pctNeg = k.pct_neg;
    }

    const priceLine = regionLine(data.series || [], {
      region, valField: 'avg', agg: 'avg', label: scopeLabel, color: lineColor,
    });
    const intraday = regionLine(data.intraday || [], {
      region, keyField: 'hour', valField: 'avg', agg: 'avg',
      label: scopeLabel, color: lineColor,
    });

    // Price-band distribution: all regions when NEM (a comparison), else just
    // the selected region.
    const distRegions = isNem
      ? [...new Set((data.distribution || []).map((d) => d.region))].sort()
      : [region];
    const totals = {};
    distRegions.forEach((rg) => {
      totals[rg] = (data.distribution || [])
        .filter((d) => d.region === rg).reduce((s, d) => s + d.n, 0) || 1;
    });
    const distSeries = BANDS.map((band, i) => ({
      label: band,
      color: CHART_PALETTE[i % CHART_PALETTE.length],
      data: distRegions.map((rg) => {
        const row = (data.distribution || []).find((d) => d.region === rg && d.band === band);
        return row ? +(100 * row.n / totals[rg]).toFixed(2) : 0;
      }),
    }));

    body = (
      <>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 3 }}>
          <StatCard label={`${scopeLabel} · avg price`}
            value={avg == null ? '—' : `A$${avg.toFixed(0)}`} unit="/MWh"
            color={lineColor} />
          <StatCard label="Intervals ≥ $300"
            value={pctHigh == null ? '—' : `${pctHigh.toFixed(2)}%`} />
          <StatCard label="Negative-price intervals"
            value={pctNeg == null ? '—' : `${pctNeg.toFixed(2)}%`} />
        </Box>

        <ChartSection title="Average daily price"
          subtitle={isNem
            ? 'Dispatch RRP, mean across all NEM regions, daily (A$/MWh)'
            : `Dispatch RRP — ${scopeLabel}, daily mean (A$/MWh)`}>
          <LineChartBox x={priceLine.x} series={priceLine.series} yFormat={formatAUD} />
        </ChartSection>

        <ChartSection title="Price-band distribution"
          subtitle={isNem
            ? 'Share of dispatch intervals by price band, per region (% of intervals)'
            : `Share of dispatch intervals by price band — ${scopeLabel}`}>
          <BarChartBox x={distRegions.map((r) => REGION_LABELS[r] || r)} series={distSeries}
            stacked yFormat={(v) => `${v}%`} height={340} />
        </ChartSection>

        <ChartSection title="Average intraday shape"
          subtitle={`Mean price by hour of day (AEST), last 30 days — ${scopeLabel}`}>
          <LineChartBox x={intraday.x} series={intraday.series} yFormat={formatAUD} height={340} />
        </ChartSection>
      </>
    );
  }

  return (
    <MarketShell
      title="Price & Volatility"
      subtitle="NEM wholesale dispatch prices — real AEMO actuals"
      days={days} setDays={setDays} region={region} setRegion={setRegion}
      loading={loading} error={error}
    >
      {body}
    </MarketShell>
  );
}
